import { logger } from './logger.js';

/**
 * Utilitaire pour détecter le statut Pro d'un utilisateur via l'API
 */
export class ClientPlanDetector {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.cache = new Map(); // Cache pour éviter les requêtes répétées

    // Log pour debug
    logger.info('ClientPlanDetector initialized', {
      baseUrl: this.baseUrl,
      baseUrlType: typeof this.baseUrl,
    });
  }

  /**
   * Détecte le statut Pro d'un utilisateur via son ID
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<{isPro: boolean, plan: string|null, name: string|null}>}
   */
  async detectUserStatus(userId) {
    if (!userId) {
      logger.warn('ClientPlanDetector: userId is required');
      return { isPro: false, plan: null, name: null };
    }

    // Vérifier le cache d'abord
    const cacheKey = `user_${userId}`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      logger.info('ClientPlanDetector: Using cached result', {
        userId,
        cached,
      });
      return cached;
    }

    try {
      logger.info('ClientPlanDetector: Fetching user info from API', {
        userId,
      });

      // Appeler l'API pour récupérer les informations de l'utilisateur
      const response = await fetch(
        `${this.baseUrl}/api/users/${userId}/status-simple`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        logger.warn('ClientPlanDetector: API request failed', {
          userId,
          status: response.status,
          statusText: response.statusText,
        });
        // En cas d'erreur, considérer comme non-pro (sécurité)
        return { isPro: false, plan: null, name: null };
      }

      const data = await response.json();

      const result = {
        isPro: data.isPro || false,
        plan: data.plan || null,
        name: data.name || null,
      };

      // Mettre en cache pour 5 minutes
      this.cache.set(cacheKey, result);
      setTimeout(() => {
        this.cache.delete(cacheKey);
      }, 5 * 60 * 1000); // 5 minutes

      logger.info('ClientPlanDetector: User info fetched successfully', {
        userId,
        result,
      });

      return result;
    } catch (error) {
      logger.error('ClientPlanDetector: Failed to fetch user info', {
        userId,
        error: error.message,
      });

      // En cas d'erreur, considérer comme non-pro (sécurité)
      return { isPro: false, plan: null, name: null };
    }
  }

  /**
   * Détecte le statut Pro d'un client via son ID (pour compatibilité descendante)
   * @param {string} clientId - ID du client
   * @returns {Promise<{isPro: boolean, plan: string|null, name: string|null}>}
   */
  async detectClientStatus(clientId) {
    if (!clientId) {
      logger.warn('ClientPlanDetector: clientId is required');
      return { isPro: false, plan: null, name: null };
    }

    // Vérifier le cache d'abord
    const cacheKey = `client_${clientId}`;
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      logger.info('ClientPlanDetector: Using cached result', {
        clientId,
        cached,
      });
      return cached;
    }

    try {
      logger.info('ClientPlanDetector: Fetching client info from API', {
        clientId,
      });

      // Appeler la nouvelle API widget pour récupérer les informations de l'opérateur
      const response = await fetch(
        `${this.baseUrl}/api/widget/operator-status?operatorId=${clientId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        logger.warn('ClientPlanDetector: API request failed', {
          clientId,
          status: response.status,
          statusText: response.statusText,
        });
        // En cas d'erreur, considérer comme non-pro (sécurité)
        return { isPro: false, plan: null, name: null };
      }

      const apiResponse = await response.json();

      // La nouvelle API retourne { success: true, data: { operator: {...} } }
      if (!apiResponse.success || !apiResponse.data?.operator) {
        logger.warn('ClientPlanDetector: Invalid API response format', {
          clientId,
          response: apiResponse,
        });
        return { isPro: false, plan: null, name: null };
      }

      const operatorData = apiResponse.data.operator;
      const result = {
        isPro: operatorData.isPro || false,
        plan: operatorData.planType || null,
        name: operatorData.displayName || null,
      }; // Mettre en cache pour 5 minutes
      this.cache.set(cacheKey, result);
      setTimeout(() => {
        this.cache.delete(cacheKey);
      }, 5 * 60 * 1000); // 5 minutes

      logger.info('ClientPlanDetector: Client info fetched successfully', {
        clientId,
        result,
      });

      return result;
    } catch (error) {
      logger.error('ClientPlanDetector: Failed to fetch client info', {
        clientId,
        error: error.message,
      });

      // En cas d'erreur, considérer comme non-pro (sécurité)
      return { isPro: false, plan: null, name: null };
    }
  }

  /**
   * Vide le cache pour un utilisateur spécifique
   * @param {string} userId - ID de l'utilisateur
   */
  clearUserCache(userId) {
    const cacheKey = `user_${userId}`;
    this.cache.delete(cacheKey);
    logger.info('ClientPlanDetector: Cache cleared for user', { userId });
  }

  /**
   * Vide le cache pour un client spécifique
   * @param {string} clientId - ID du client
   */
  clearClientCache(clientId) {
    const cacheKey = `client_${clientId}`;
    this.cache.delete(cacheKey);
    logger.info('ClientPlanDetector: Cache cleared for client', { clientId });
  }

  /**
   * Vide tout le cache
   */
  clearAllCache() {
    this.cache.clear();
    logger.info('ClientPlanDetector: All cache cleared');
  }
}
